import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Grid,
    FormControl, Select, MenuItem, OutlinedInput
} from '@material-ui/core';
import LabelValue from "../../../shared/labelvalue/LabelValue.js";
import FileSelector from "../../../shared/fileselector/FileSelector.js";
import InstructionMessage from '../../../shared/instructionmessage/InstructionMessage.js';
import CloudUpload from '@material-ui/icons/CloudUploadOutlined';
import Add from '@material-ui/icons/AddOutlined';
import Close from '@material-ui/icons/CloseOutlined';
import ContainedButton from '../../../shared/buttons/ContainedButton';
import TextButton from '../../../shared/buttons/TextButton';
import { truncarDescricao } from './documentosRegras';

const useStyles = makeStyles(theme => ({
    dialogContent: {
        paddingTop: theme.spacing(2)
    }
}));

export default function DialogAdicionarDocumento(props) {
    const {
        open,
        onClose,
        onSave,
        pessoaNome = '',
        composicaoFamiliar = [],
        disponiveis = [],
        initialValues = {}
    } = props;

    const classes = useStyles();

    const [selectedDocType, setSelectedDocType] = useState('');
    const [desc, setDesc] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedFamiliarIndex, setSelectedFamiliarIndex] = useState('');
    const [selectedPendingDoc, setSelectedPendingDoc] = useState(null);

    useEffect(() => {
        if (open) {
            setSelectedDocType(initialValues.selectedDocType || '');
            // A descrição de uma pendência antiga pode vir maior que o limite atual de 40.
            setDesc(truncarDescricao(initialValues.desc || ''));
            setSelectedFile(null);
            setSelectedFamiliarIndex(initialValues.selectedFamiliarIndex || '');
            setSelectedPendingDoc(initialValues.selectedPendingDoc || null);
        }
    }, [open, initialValues]);

    const handleAdd = () => {
        onSave({
            selectedDocType,
            selectedFamiliarIndex,
            desc,
            selectedFile,
            selectedPendingDoc
        });
    };

    return (
        <Dialog
            open={open}
            scroll="body"
            onClose={onClose}
            fullWidth maxWidth="sm"
        >
            <DialogTitle>{selectedPendingDoc ? "Corrigir Documento Pendente" : "Selecionar Documento"}</DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <InstructionMessage
                    title="Orientações sobre adicionar documentos"
                    type="info"
                    variant="standard"
                    collapsible={true}
                    defaultExpanded={true}
                    items={[
                        "Os documentos enviados só podem ser PDF's.",
                        "O tamanho máximo de um documento é de 5 MB.",
                        "Todos os documentos devem ser legíveis."
                    ]}
                />
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <LabelValue label="Tipo de Documento *">
                            <FormControl variant="outlined" fullWidth>
                                <Select
                                    value={selectedDocType}
                                    onChange={(e) => setSelectedDocType(e.target.value)}
                                    disabled={!!selectedPendingDoc}
                                >
                                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                                    {selectedPendingDoc ? (
                                        <MenuItem value={selectedPendingDoc.documento?.id}>
                                            {selectedPendingDoc.documento?.nome}
                                        </MenuItem>
                                    ) : (
                                        disponiveis.map(doc => (
                                            <MenuItem key={doc.id} value={doc.id}>{doc.nome}</MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12}>
                        <LabelValue label="Pertence a *">
                            <FormControl variant="outlined" fullWidth>
                                <Select
                                    value={selectedFamiliarIndex}
                                    onChange={(e) => setSelectedFamiliarIndex(e.target.value)}
                                    disabled={selectedFamiliarIndex !== ''}
                                >
                                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                                    <MenuItem value="-1">{`${pessoaNome || 'Você'} - Candidato`}</MenuItem>
                                    {(composicaoFamiliar || []).map((member, idx) => (
                                        <MenuItem key={idx} value={String(idx)}>
                                            {`${member.descricao} - ${member.grauParentesco?.descricao || ''}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12}>
                        <LabelValue label="Descrição">
                            <FormControl variant="outlined" fullWidth>
                                <OutlinedInput
                                    value={desc}
                                    onChange={(e) => setDesc(e.target.value)}
                                    inputProps={{ maxLength: 40 }}
                                    placeholder="Ex: Cópia frente e verso"
                                    disabled={!!selectedPendingDoc}
                                />
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12}>
                        <LabelValue label="Documento *">
                            <FileSelector
                                onSelect={(file) => setSelectedFile(file)}
                                onRemove={() => setSelectedFile(null)}
                                onFile={selectedFile ? { nome: selectedFile.name } : null}
                                maxSize={5 * 1024 * 1024}
                                buttonIcon={CloudUpload}
                                hideButtonTextOnMobile
                                htmlFor="anexoUpload"
                            />
                        </LabelValue>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <TextButton onClick={onClose} startIcon={<Close />}>
                    Fechar
                </TextButton>
                <ContainedButton
                    onClick={handleAdd}
                    startIcon={<Add />}
                    disabled={!selectedDocType || !selectedFile || selectedFamiliarIndex === ''}
                >
                    Adicionar
                </ContainedButton>
            </DialogActions>
        </Dialog>
    );
}

DialogAdicionarDocumento.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    pessoaNome: PropTypes.string,
    composicaoFamiliar: PropTypes.array,
    disponiveis: PropTypes.array,
    initialValues: PropTypes.object
};
